import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
const FolderTreeContext = createContext(null);
const STORAGE_KEY = 'studyFolders.expandedIds';
// Helper to collect all folder IDs that have children
function collectParentIds(nodes) {
    const ids = [];
    for (const node of nodes) {
        if (node.children.length > 0) {
            ids.push(node.id);
            ids.push(...collectParentIds(node.children));
        }
    }
    return ids;
}
export function FolderTreeProvider({ children, activeFolderId, onActiveFolderChange, folders, }) {
    // User's explicit expand/collapse decisions. Any folder not in this map
    // falls back to the auto-expand default (parents are expanded by default).
    const [userOverrides, setUserOverrides] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    // Current format: array of [id, expanded] tuples from Map serialization.
                    if (parsed.every((e) => Array.isArray(e) && e.length === 2)) {
                        return new Map(parsed.map(([id, expanded]) => [
                            String(id),
                            Boolean(expanded),
                        ]));
                    }
                    // Legacy format: an array of expanded IDs — treat each as an explicit expand.
                    return new Map(parsed.map((id) => [String(id), true]));
                }
            }
        }
        catch {
            // Ignore storage errors
        }
        return new Map();
    });
    // Auto-expand set: every folder that has children is open by default.
    const autoExpandedIds = useMemo(() => {
        if (!folders)
            return new Set();
        return new Set([...collectParentIds(folders.personal), ...collectParentIds(folders.clinic)]);
    }, [folders]);
    // Effective expanded set = auto-expanded ∪ explicit user opens − explicit user collapses.
    const expandedIds = useMemo(() => {
        const result = new Set(autoExpandedIds);
        userOverrides.forEach((expanded, id) => {
            if (expanded)
                result.add(id);
            else
                result.delete(id);
        });
        return result;
    }, [autoExpandedIds, userOverrides]);
    const [draggedFolderId, setDraggedFolderId] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);
    const [dropPosition, setDropPosition] = useState(null);
    const setDropTarget = useCallback((id, position) => {
        setDropTargetId(id);
        setDropPosition(position);
    }, []);
    // Persist user overrides to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify([...userOverrides]));
        }
        catch {
            // Ignore storage errors
        }
    }, [userOverrides]);
    const isExpanded = useCallback((id) => expandedIds.has(id), [expandedIds]);
    const setExpanded = useCallback((id, expanded) => {
        setUserOverrides((prev) => {
            const next = new Map(prev);
            next.set(id, expanded);
            return next;
        });
    }, []);
    const toggleExpanded = useCallback((id) => {
        setExpanded(id, !isExpanded(id));
    }, [isExpanded, setExpanded]);
    return (_jsx(FolderTreeContext.Provider, { value: {
            expandedIds,
            toggleExpanded,
            setExpanded,
            isExpanded,
            activeFolderId,
            setActiveFolderId: onActiveFolderChange,
            draggedFolderId,
            setDraggedFolderId,
            dropTargetId,
            dropPosition,
            setDropTarget,
        }, children: children }));
}
// eslint-disable-next-line react-refresh/only-export-components
export function useFolderTreeContext() {
    const context = useContext(FolderTreeContext);
    if (!context) {
        throw new Error('useFolderTreeContext must be used within a FolderTreeProvider');
    }
    return context;
}
